import React from 'react';
import classes from './NotificationBoxes.module.scss';
import {
  PostCardMediaPreviewViewProps,
  PostCardPreviewMediaDataType
} from '../PostCardMediaPreview/PostCardMediaPreview.view';
import { BoxNotificationViewProps } from '../../../../components/containers/BoxNotification/BoxNotification.view';
import { BoxNotification } from '../../../../components/containers/BoxNotification';
import classNames from 'classnames';
import { PostFeedType, PostTikTokPrivacy } from '../../../../models/graphql';
import {
  getInstagramRationObject,
  isInvalidInstagramMedia
} from '../PostCardMediaPreviewInstagram/PostCardMediaPreviewInstagram.view';
import { isInvalidFacebookMedia } from '../PostCardMediaPreviewFacebook/PostCardMediaPreviewFacebook.view';
import { isInvalidLinkedInMedia } from '../PostCardMediaPreviewLinkedin/PostCardMediaPreviewLinkedin.view';
import { TikTokProfileValues } from '../../../../components/socialProfiles/SocialProfileListItem/SocialProfileListItem.view';
import {
  FixedTikTokRatio,
  isInvalidTikTokMedia,
  PerfectTikTokRatioEdge
} from '../PostCardMediaPreviewTikTok/PostCardMediaPreviewTikTok.view';
import { isInvalidTwitterMedia } from '../PostCardMediaPreviewTwitter/PostCardMediaPreviewTwitter.view';

export type NotificationBoxesViewProps = {
  //

  selectedVariation: PostCardPreviewMediaDataType;
  socialProfile: PostCardMediaPreviewViewProps['socialProfile'];
  igType?: PostFeedType;
  privacy?: PostTikTokPrivacy | null;
  allowedActions?: TikTokProfileValues['allowedActions'];
};

type PreviewNotificationType = {
  message: string | JSX.Element | JSX.Element[];
  type: BoxNotificationViewProps['type'];
};

export const NotificationBoxesView = (props: NotificationBoxesViewProps): JSX.Element => {
  const { selectedVariation, socialProfile, igType } = props;
  const { media } = selectedVariation;
  let notifications: PreviewNotificationType[] = [];
  switch (socialProfile.__typename) {
    case 'FacebookSocialProfile': {
      notifications =
        media.length > 1 && !isInvalidFacebookMedia(media)
          ? [
              {
                type: 'warning',
                message: (
                  <p className={classNames('no-b-m', classes.WarningText)}>
                    The layout & crop may look different when published.
                  </p>
                )
              }
            ]
          : [];
      break;
    }
    case 'InstagramSocialProfile': {
      if (!('igType' in props)) throw new Error('IG type not provided');
      const isIvalidMedia = isInvalidInstagramMedia(media);
      switch (igType) {
        case PostFeedType.FEED:
        case PostFeedType.REEL: {
          const { offLimits } = getInstagramRationObject(media, igType);
          notifications =
            media.length > 1 && !isIvalidMedia
              ? [
                  {
                    type: 'warning',
                    message: (
                      <p className={classNames('no-b-m', classes.WarningText)}>
                        All images in a Carousel Post will be cropped to the size of the first image.
                      </p>
                    )
                  }
                ]
              : media.length === 1 && offLimits && igType === PostFeedType.FEED
              ? [
                  {
                    type: 'warning',
                    message: (
                      <p className={classNames('no-b-m', classes.WarningText)}>
                        Images in the Post will be cropped according to Instagram limits.
                      </p>
                    )
                  }
                ]
              : [];
          /* TODO WE ADDED this hardcoded `notifications = [];` to temporary disable IG notifications 05/02/2025 */
          notifications = [];
          break;
        }
        default:
        case PostFeedType.STORY: {
          break;
        }
      }
      break;
    }
    case 'PinterestSocialProfile':
    case 'GoogleSocialProfile': {
      break;
    }
    case 'LinkedInSocialProfile': {
      notifications =
        media.length > 1 && !isInvalidLinkedInMedia(media)
          ? [
              {
                type: 'warning',
                message: (
                  <p className={classNames('no-b-m', classes.WarningText)}>
                    The layout & crop may look different when published.
                  </p>
                )
              }
            ]
          : [];
      break;
    }
    case 'TikTokSocialProfile': {
      if (!('privacy' in props)) throw new Error('tik tok privacy not provided');
      if (!('allowedActions' in props)) throw new Error('tik tok allowedActions not provided');
      const { privacy, allowedActions } = props;
      const profileWarnings: string[] = [];
      let warnings: string[] = [];
      const imageAspectRatio = media.length ? media[0].width / media[0].height : FixedTikTokRatio;
      if (!privacy && privacy !== undefined) {
        notifications = [
          ...notifications,
          {
            type: 'error',
            message: 'You need to select an audience.'
          }
        ];
      }
      if (allowedActions !== undefined && !allowedActions.includes('comment')) {
        profileWarnings.push('Comments');
      }
      if (allowedActions !== undefined && !allowedActions.includes('duet')) {
        profileWarnings.push('Duets');
      }
      if (allowedActions !== undefined && !allowedActions.includes('stitch')) {
        profileWarnings.push('Stitches');
      }
      if (profileWarnings.length) {
        warnings = [...warnings, `${profileWarnings.join('/')} are disabled for this TikTok post.`];
      }
      if (imageAspectRatio < FixedTikTokRatio && !isInvalidTikTokMedia(media)) {
        warnings = [...warnings, 'TikTok videos taller than 1:1.95 may have the top and bottom cut off.'];
      } else if (
        imageAspectRatio > FixedTikTokRatio &&
        imageAspectRatio < PerfectTikTokRatioEdge &&
        !isInvalidTikTokMedia(media)
      ) {
        warnings = [...warnings, 'TikTok videos that are around 9:16 may have the sides cut off.'];
      }
      if (warnings.length) {
        notifications = [
          ...notifications,
          {
            type: 'warning',
            message: warnings.map((m, index, arr) => (
              <p
                key={index}
                className={index + 1 === arr.length ? classNames('no-b-m', classes.WarningText) : classes.WarningText}>
                {m}
              </p>
            ))
          }
        ];
      }
      break;
    }
    case 'TwitterSocialProfile': {
      notifications =
        media.length > 1 && !isInvalidTwitterMedia(media)
          ? [
              {
                type: 'warning',
                message: (
                  <p className={classNames('no-b-m', classes.WarningText)}>
                    The layout & crop may look different when published.
                  </p>
                )
              }
            ]
          : [];
    }
  }
  return (
    <>
      {notifications.map(({ type, message }, index) => (
        <BoxNotification customClassNames={classes.NotificationBoxes} type={type} key={index}>
          {message}
        </BoxNotification>
      ))}
    </>
  );
};
